/**
 * Attorney Portal Layout
 * Matches the main app layout with blue sidebar
 * Context-aware: Analysis tools only show when viewing a case
 * Sidebar uses scroll-to-section navigation with IntersectionObserver highlighting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Home,
  FileText,
  Scale,
  Download,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  User,
  Building2,
  BarChart3,
  Target,
  Shield,
  Clock,
  TrendingUp,
  History,
  Lightbulb,
  Wrench,
  Gavel,
  AlertTriangle,
  Search,
  BookOpen,
  Mail,
  Settings,
  Dna,
  Brain,
  ClipboardList,
  Calendar
} from 'lucide-react';
import DigestSettings from './DigestSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AttorneyLayoutProps {
  children: React.ReactNode;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

interface AttorneyProfile {
  id: string;
  email: string;
  name: string;
  law_firm_id: string;
  law_firm_name: string;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  path?: string;
  section?: string;
  badge?: string | number;
}

export default function AttorneyLayout({ children, activeSection, onSectionChange }: AttorneyLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState<AttorneyProfile | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [casesCount, setCasesCount] = useState(0);
  const [showDigestSettings, setShowDigestSettings] = useState(false);
  const [visibleSection, setVisibleSection] = useState<string>('');

  // Determine if we're viewing a case detail (exact case detail page, not sub-pages)
  const isViewingCase = location.pathname.includes('/attorney/case/');
  const isOnCaseDetailPage = isViewingCase && !location.pathname.includes('/settlement') && !location.pathname.includes('/tools') && !location.pathname.includes('/demand-letter') && !location.pathname.includes('/timeline');
  const currentCaseId = isViewingCase
    ? location.pathname.split('/attorney/case/')[1]?.split('/')[0]
    : null;

  // IntersectionObserver for scroll-based section highlighting
  useEffect(() => {
    if (!isOnCaseDetailPage) return;

    const sectionIds = [
      'overview', 'pro-forma', 'attorney-analysis', 'adjuster-analysis',
      'fair-settlement', 'monte-carlo', 'timeline', 'sensitivity',
      'historical', 'what-if', 'strategy-builder', 'litigation-econ',
      'game-theory', 'bad-faith', 'case-law', 'evidence', 'demand-letter'
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setVisibleSection(visible[0].target.id);
        }
      },
      {
        rootMargin: '-100px 0px -40% 0px',
        threshold: [0, 0.25, 0.5]
      }
    );

    // Observe all section elements
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isOnCaseDetailPage, location.pathname]);

  useEffect(() => {
    // Check auth
    const token = localStorage.getItem('attorney_token');
    const profileStr = localStorage.getItem('attorney_profile');

    if (!token || !profileStr) {
      navigate('/attorney/login');
      return;
    }

    try {
      const parsed = JSON.parse(profileStr);
      setProfile(parsed);
    } catch {
      navigate('/attorney/login');
    }

    // Fetch cases count
    fetchCasesCount();
  }, []);

  const fetchCasesCount = async () => {
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCasesCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching cases count:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('attorney_token');
    localStorage.removeItem('attorney_profile');
    navigate('/attorney/login');
  };

  // Scroll to a section by ID
  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Navigation items - Main (always visible)
  const mainNavItems: NavItem[] = [
    { icon: Home, label: 'Dashboard', path: '/attorney/dashboard', badge: casesCount || undefined },
  ];

  // Case Summary section - scroll-to-section items
  const caseSummaryNavItems: NavItem[] = [
    { icon: FileText, label: 'Overview', section: 'overview' },
    { icon: BarChart3, label: 'Pro Forma', section: 'pro-forma' },
  ];

  // Analysis section - scroll-to-section items
  const analysisNavItems: NavItem[] = [
    { icon: Scale, label: 'Attorney Analysis', section: 'attorney-analysis' },
    { icon: Shield, label: 'Adjuster Analysis', section: 'adjuster-analysis' },
    { icon: Target, label: 'Fair Settlement', section: 'fair-settlement' },
    { icon: BarChart3, label: 'Monte Carlo', section: 'monte-carlo' },
    { icon: Calendar, label: 'Timeline', section: 'timeline' },
  ];

  // Settlement Tools section - scroll-to-section items
  const settlementToolsNavItems: NavItem[] = [
    { icon: TrendingUp, label: 'Sensitivity', section: 'sensitivity' },
    { icon: History, label: 'Historical', section: 'historical' },
    { icon: Lightbulb, label: 'What-If', section: 'what-if' },
    { icon: Wrench, label: 'Strategy Builder', section: 'strategy-builder' },
    { icon: Scale, label: 'Litigation Econ', section: 'litigation-econ' },
    { icon: Brain, label: 'Game Theory', section: 'game-theory' },
  ];

  // Advanced section - scroll-to-section items
  const advancedNavItems: NavItem[] = [
    { icon: AlertTriangle, label: 'Bad Faith', section: 'bad-faith' },
    { icon: Search, label: 'Case Law', section: 'case-law' },
    { icon: ClipboardList, label: 'Evidence', section: 'evidence' },
    { icon: FileText, label: 'Demand Letter', section: 'demand-letter' },
  ];

  // Resources - only visible when viewing a case
  const resourceNavItems: NavItem[] = [
    { icon: Download, label: 'Download PDF', section: 'download-pdf' },
    { icon: FileText, label: 'Download Word', section: 'download-docx' },
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.section) {
      // Handle download actions
      if (item.section === 'download-pdf' || item.section === 'download-docx') {
        if (onSectionChange) {
          onSectionChange(item.section);
        }
        setMobileMenuOpen(false);
        return;
      }

      // If not on the case detail page, navigate there first with hash
      if (!isOnCaseDetailPage && currentCaseId) {
        navigate(`/attorney/case/${currentCaseId}#${item.section}`);
      } else {
        // Already on case detail - scroll to section
        scrollToSection(item.section);
        if (onSectionChange) {
          onSectionChange(item.section);
        }
      }
    }
    setMobileMenuOpen(false);
  };

  // Determine the effective active section (from IntersectionObserver or prop)
  const effectiveActiveSection = visibleSection || activeSection || '';

  const NavItemButton = ({ item }: { item: NavItem }) => {
    const isActive = item.path
      ? location.pathname === item.path || (item.path !== '/attorney/dashboard' && location.pathname.startsWith(item.path))
      : (isOnCaseDetailPage && item.section === effectiveActiveSection);

    return (
      <button
        onClick={() => handleNavClick(item)}
        className={`
          w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm
          ${isActive
            ? 'bg-white/15 text-white'
            : 'text-blue-200 hover:bg-white/10 hover:text-white'
          }
        `}
        title={sidebarCollapsed ? item.label : undefined}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}

        {!sidebarCollapsed && item.badge !== undefined && (
          <span className="ml-auto bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[#0b2545] text-white transition-all duration-300 flex flex-col h-full
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <div 
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/attorney/dashboard')}
          title="Go to Dashboard"
        >
          {sidebarCollapsed && !mobile ? (
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-10 w-10 object-contain hover:opacity-80 transition-opacity"
            />
          ) : (
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-44 w-auto max-w-[220px] object-contain hover:opacity-80 transition-opacity"
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
        {/* Main Section - Always visible */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 text-xs font-semibold text-blue-300 uppercase tracking-wider">
            Main
          </p>
        )}
        <div className="space-y-0.5">
          {mainNavItems.map((item) => (
            <NavItemButton key={item.label} item={item} />
          ))}
        </div>

        {/* Case Summary Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Case Summary
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {caseSummaryNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Analysis Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Analysis
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {analysisNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Settlement Tools Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Settlement Tools
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {settlementToolsNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Advanced Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Advanced
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {advancedNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Downloads Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Downloads
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {resourceNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && profile && (
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{profile.name}</p>
              <p className="text-xs text-blue-300 truncate">{profile.law_firm_name}</p>
            </div>
          </div>
          
          {/* Digest Settings Button */}
          <button
            onClick={() => setShowDigestSettings(true)}
            className="w-full mt-2 flex items-center gap-3 px-3 py-2 rounded-lg text-blue-200 hover:bg-white/10 hover:text-white transition-colors"
            data-testid="open-digest-settings"
          >
            <Mail className="w-5 h-5" />
            <span>Weekly Digest</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-3 px-3 py-2 rounded-lg text-blue-200 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      )}

      {/* Collapse button - desktop only */}
      {!mobile && (
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-blue-200 hover:bg-white/10 hover:text-white transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="ml-2 text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block h-screen sticky top-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="h-full" onClick={(e) => e.stopPropagation()}>
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-6 h-16">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Page title - mobile */}
            <div className="lg:hidden flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-900">Attorney Portal</span>
            </div>

            {/* Breadcrumb / Title - desktop */}
            <div className="hidden lg:flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">{profile?.law_firm_name || 'Attorney Portal'}</span>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {profile && (
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-slate-900">{profile.name}</p>
                  <p className="text-xs text-slate-500">{profile.email}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white px-4 lg:px-6 py-3">
          <p className="text-xs text-slate-500 text-center">
            © 2026 ClaimSync. Attorney Portal - All data is confidential.
          </p>
        </footer>
      </div>
      
      {/* Digest Settings Modal */}
      {profile && (
        <DigestSettings
          attorneyId={profile.id}
          isOpen={showDigestSettings}
          onClose={() => setShowDigestSettings(false)}
        />
      )}
    </div>
  );
}
